/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { FlexRow } from '../../basic/Flex';
import type { FlattenObjectKeys } from '@shared/types';
import { fieldSizes } from '@/shared/themes/light/layout';
import { useFormStoreFieldError, useFormStoreFieldState } from "../FormStore";

type ThemeSizes = FlattenObjectKeys<typeof fieldSizes>;
type ThemeVariants = "outlined" | "filled" | "standard";
type InputContainerSize = ThemeSizes | "auto" | "small";

type InputContainerProps = {
  name: string;
  variant?: ThemeVariants;
  size?: InputContainerSize;
  status?: "default" | "error" | "success";
};

const InputContainer: ComponentWithChildren<InputContainerProps> = ({
  name,
  children,
  variant = "outlined",
  size = "small",
  status,
  sx,
  ...props
}) => {
  const fieldState = useFormStoreFieldState(name);
  const fieldError = useFormStoreFieldError(name);
  const isRequiredMissing = fieldError === '__REQUIRED__';
  const hasRealError = fieldError !== undefined && fieldError !== '__REQUIRED__';
  
  return (
    <FlexRow
      data-input-container
      data-field-name={name}
      data-required-missing={isRequiredMissing ? 'true' : undefined}
      className={`input-container${isRequiredMissing ? ' input-container-missing border-[var(--status-warning-border)]' : ''}`.trim()}
      sx={{
        variant: `forms.inputContainer.variants.${variant}`,
        height: `${size}`,
        display: "flex",
        overflow: "hidden",
        flexShrink: 0,
        flexGrow: 1,
        width: "100%",
        maxWidth: "100%",
        transition: "border-color 200ms ease",
        ...(fieldState?.isFocused && {
          borderWidth: "1.5px",
          borderColor: "primary.main",
          color: "primary.dark2",
        }),
        ...(status === "success" && {
          borderColor: "success.main",
          color: "success.dark1",
        }),
        // Only show red border for real errors (not required field validation)
        ...(hasRealError && {
          borderColor: "error.main",
          color: "error.dark1",
        }),
        ...sx,
      }}
      {...props}
    >
      {children}
    </FlexRow>
  );
};

export default InputContainer;
